import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { classService } from '../services/classService';
import { schoolService } from '../services/schoolService';
import { studentService } from '../services/studentService';
import { Class, ClassRequest, School, StudentImportResult } from '../types';
import { PlusIcon, UsersIcon, CalendarIcon, BarChartIcon } from '../components/Icons';
import BatchCreateClassDialog from '../components/BatchCreateClassDialog';
import TeacherSearchSelect from '../components/TeacherSearchSelect';

const GRADE_DISPLAY_MAP: Record<string, string> = {
  '七年级': '七年级（初一）',
  '八年级': '八年级（初二）',
  '九年级': '九年级（初三）',
};

const getGradeDisplay = (grade: string) => GRADE_DISPLAY_MAP[grade] || grade;

const ClassListPage: React.FC = () => {
  const navigate = useNavigate();
  const { schoolId, year } = useParams<{ schoolId: string; year: string }>();
  const [school, setSchool] = useState<School | null>(null);
  const [classes, setClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingClass, setEditingClass] = useState<Class | null>(null);
  const [formData, setFormData] = useState<ClassRequest>({
    name: '',
    academicYear: year || '',
    grade: '',
    schoolId: parseInt(schoolId || '0'),
    headTeacherId: undefined,
    description: '',
  });
  // 编辑态下用于初始化老师下拉的展示
  const [headTeacherDisplay, setHeadTeacherDisplay] = useState<string>('');

  // 学年列表和切换
  const [availableYears, setAvailableYears] = useState<string[]>([]);
  const [selectedYear, setSelectedYear] = useState<string>(year || '');

  // Excel导入相关
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<StudentImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // 批量创建班级
  const [showBatchModal, setShowBatchModal] = useState(false);

  useEffect(() => {
    fetchData();
    generateAvailableYears();
  }, [schoolId, year, currentPage]);

  useEffect(() => {
    setSelectedYear(year || '');
  }, [year]);

  const generateAvailableYears = () => {
    const thisYear = new Date().getFullYear();
    const years: string[] = [];
    for (let i = -2; i <= 2; i++) {
      years.push((thisYear + i).toString());
    }
    setAvailableYears(years);
  };

  const handleYearChange = (newYear: string) => {
    navigate(`/schools/${schoolId}/years/${newYear}/classes`);
  };

  const fetchData = async () => {
    if (!schoolId || !year) return;

    setLoading(true);
    try {
      // 获取学校信息
      const schoolData = await schoolService.getSchool(parseInt(schoolId));
      setSchool(schoolData);

      // 获取班级列表
      const response = await classService.getClasses({
        schoolId: parseInt(schoolId),
        year: year,
        page: currentPage,
        size: 12,
      });
      setClasses(response.content);
      setTotalPages(response.totalPages);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || '获取班级列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingClass(null);
    setFormData({
      name: '',
      academicYear: year || '',
      grade: '',
      schoolId: parseInt(schoolId || '0'),
      headTeacherId: undefined,
      description: '',
    });
    setHeadTeacherDisplay('');
    setShowModal(true);
  };

  const handleEdit = (clazz: Class) => {
    setEditingClass(clazz);
    setFormData({
      name: clazz.name,
      academicYear: clazz.academicYear,
      grade: clazz.grade || '',
      schoolId: clazz.schoolId,
      headTeacherId: clazz.headTeacherId,
      description: clazz.description || '',
    });
    setHeadTeacherDisplay(clazz.headTeacherName || clazz.teacherName || '');
    setShowModal(true);
  };

  const handleDelete = async (classId: string) => {
    if (!window.confirm('确定要删除这个班级吗？')) return;

    try {
      await classService.deleteClass(classId);
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.message || '删除班级失败');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingClass) {
        await classService.updateClass(editingClass.classId, formData);
      } else {
        await classService.createClass(formData);
      }
      setShowModal(false);
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.message || '保存班级失败');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClassClick = (classId: string) => {
    navigate(`/schools/${schoolId}/years/${year}/classes/${classId}/students`);
  };

  // Excel导入相关
  const handleImportClick = () => {
    setImportFile(null);
    setImportResult(null);
    setShowImportModal(true);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.xlsx')) {
        alert('仅支持.xlsx格式的Excel文件');
        return;
      }
      setImportFile(file);
    }
  };

  const handleImportSubmit = async () => {
    if (!importFile || !schoolId) return;

    setImporting(true);
    try {
      const result = await studentService.importStudents(
        parseInt(schoolId),
        importFile
      );
      setImportResult(result);
      if (result.successCount > 0 || result.associatedCount > 0) {
        fetchData();
      }
    } catch (err: any) {
      alert(err.response?.data?.message || '导入失败');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await studentService.downloadImportTemplate();
    } catch (err) {
      alert('下载模板失败');
    }
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/schools')}
          className="text-sm text-on-surface-variant hover:text-on-surface mb-4 flex items-center space-x-1"
        >
          <span>←</span>
          <span>返回学校列表</span>
        </button>
        <h1 className="text-2xl font-bold text-on-surface font-headline">
          {school?.name}
        </h1>
        <p className="text-sm text-on-surface-variant font-body mt-1">管理班级和教师</p>
      </div>

      {/* 操作栏 */}
      <div className="bg-surface-container-lowest rounded-3xl shadow-soft p-4 mb-6">
        <div className="flex items-center gap-3">
          {/* 功能入口 */}
          <button
            onClick={() => navigate(`/schools/${schoolId}/teachers`)}
            className="h-11 px-5 bg-surface-container-high text-on-surface rounded-xl hover:bg-surface-container-low transition-colors font-medium flex items-center space-x-2"
          >
            <UsersIcon size={18} />
            <span>教师管理</span>
          </button>
          <button
            onClick={() => navigate(`/schools/${schoolId}/data`)}
            className="h-11 px-5 bg-surface-container-high text-on-surface rounded-xl hover:bg-surface-container-low transition-colors font-medium flex items-center space-x-2"
          >
            <BarChartIcon size={18} />
            <span>数据管理</span>
          </button>

          {/* 分隔线 */}
          <div className="h-8 w-px bg-gray-200 mx-1" />

          {/* 筛选 */}
          <div className="h-11 flex items-center gap-2 px-5 bg-surface-container-high rounded-xl">
            <CalendarIcon size={18} className="text-on-surface-variant" />
            <select
              value={selectedYear}
              onChange={(e) => handleYearChange(e.target.value)}
              className="bg-transparent border-none focus:outline-none text-sm font-medium text-on-surface cursor-pointer"
            >
              {availableYears.map((y) => (
                <option key={y} value={y}>
                  {y} 学年
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1" />
          <button
            onClick={handleImportClick}
            className="h-11 px-5 bg-surface-container-high text-on-surface rounded-xl hover:bg-surface-container-low transition-colors font-medium flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <span>批量导入学生</span>
          </button>
          <button
            onClick={() => setShowBatchModal(true)}
            className="h-11 px-5 bg-surface-container-high text-on-surface rounded-xl hover:bg-surface-container-low transition-colors font-medium flex items-center space-x-2"
          >
            <PlusIcon size={18} />
            <span>批量创建</span>
          </button>
          <button
            onClick={handleCreate}
            className="h-11 px-5 gradient-primary text-on-primary rounded-xl shadow-teal transition-colors font-medium flex items-center space-x-2"
          >
            <PlusIcon size={18} />
            <span>添加班级</span>
          </button>
        </div>
      </div>

      <BatchCreateClassDialog
        open={showBatchModal}
        onClose={() => setShowBatchModal(false)}
        onSuccess={() => fetchData()}
        schoolId={parseInt(schoolId || '0')}
        defaultYear={selectedYear || year || ''}
      />


      {error && (
        <div className="bg-danger-50 border border-danger-200 text-danger-800 px-4 py-3 rounded-xl mb-6">
          {error}
        </div>
      )}

      {/* Classes Grid */}
      <div className="bg-surface-container-lowest rounded-3xl shadow-soft overflow-hidden">
        {loading ? (
          <div className="text-center py-12 text-on-surface-variant font-body">加载中...</div>
        ) : classes.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-on-surface-variant mb-4 flex justify-center">
              <UsersIcon size={48} />
            </div>
            <p className="text-on-surface-variant font-body">暂无班级数据</p>
            <button
              onClick={handleCreate}
              className="mt-4 px-4 py-2 gradient-primary text-on-primary rounded-xl shadow-teal transition-colors text-sm font-medium"
            >
              创建第一个班级
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-6">
              {classes.map((clazz) => (
                <div
                  key={clazz.classId}
                  className="group rounded-2xl overflow-hidden shadow-soft hover:shadow-soft-md transition-all bg-surface-container-lowest flex flex-col"
                >
                  <div
                    onClick={() => handleClassClick(clazz.classId)}
                    className="p-5 cursor-pointer flex-1"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center text-primary-600 group-hover:bg-primary-200 transition-colors">
                        <UsersIcon size={24} />
                      </div>
                      <span className="text-xs font-medium text-on-surface-variant bg-surface-container-high px-2 py-1 rounded-xl font-label">
                        {clazz.classId}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-on-surface font-headline mb-1">{clazz.name}</h3>
                    {clazz.grade && (
                      <p className="text-sm text-on-surface-variant font-body mb-2">{getGradeDisplay(clazz.grade)}</p>
                    )}
                    {(clazz.headTeacherName || clazz.teacherName) && (
                      <p className="text-sm text-on-surface-variant font-body mb-3">
                        班主任: {clazz.headTeacherName || clazz.teacherName}
                      </p>
                    )}
                    <div className="flex items-center text-primary-600">
                      <UsersIcon size={16} className="mr-1" />
                      <span className="text-sm font-medium font-label">{clazz.studentCount} 名学生</span>
                    </div>
                  </div>
                  <div className="bg-surface-container-low px-5 py-3 flex items-center justify-end space-x-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(clazz);
                      }}
                      className="text-sm text-primary-600 hover:text-primary-900 font-medium"
                    >
                      编辑
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(clazz.classId);
                      }}
                      className="text-sm text-danger-600 hover:text-danger-900 font-medium"
                    >
                      删除
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="bg-surface-container-low px-6 py-4 flex items-center justify-between" style={{ borderTop: '1px solid rgba(171, 179, 183, 0.2)' }}>
                <div className="text-sm text-on-surface-variant font-label">共 {totalPages} 页</div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
                    disabled={currentPage === 0}
                    className="px-4 py-2 bg-surface-container-high rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-surface-container-lowest transition-colors text-sm font-medium"
                  >
                    上一页
                  </button>
                  <div className="px-4 py-2 bg-surface-container-lowest shadow-soft rounded-xl text-sm font-medium font-label">
                    {currentPage + 1} / {totalPages}
                  </div>
                  <button
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))}
                    disabled={currentPage >= totalPages - 1}
                    className="px-4 py-2 bg-surface-container-high rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-surface-container-lowest transition-colors text-sm font-medium"
                  >
                    下一页
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-container-lowest rounded-3xl max-w-md w-full shadow-xl">
            <div className="px-6 py-4" style={{ borderBottom: '1px solid rgba(171, 179, 183, 0.2)' }}>
              <h2 className="text-xl font-semibold text-on-surface font-headline">
                {editingClass ? '编辑班级' : '创建班级'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-on-surface font-label mb-2">
                    班级名称 <span className="text-danger-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-3 py-2.5 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
                    placeholder="如：一年级1班"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-on-surface font-label mb-2">
                    学年 <span className="text-danger-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="academicYear"
                    required
                    value={formData.academicYear}
                    onChange={handleChange}
                    className="w-full px-3 py-2.5 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
                    placeholder="如：2024"
                    readOnly={!!editingClass}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-on-surface font-label mb-2">
                    年级
                  </label>
                  <select
                    name="grade"
                    value={formData.grade}
                    onChange={handleChange}
                    className="w-full px-3 py-2.5 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
                  >
                    <option value="">请选择年级</option>
                    <option value="一年级">一年级</option>
                    <option value="二年级">二年级</option>
                    <option value="三年级">三年级</option>
                    <option value="四年级">四年级</option>
                    <option value="五年级">五年级</option>
                    <option value="六年级">六年级</option>
                    <option value="七年级">七年级（初一）</option>
                    <option value="八年级">八年级（初二）</option>
                    <option value="九年级">九年级（初三）</option>
                    <option value="高一">高一</option>
                    <option value="高二">高二</option>
                    <option value="高三">高三</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-on-surface font-label mb-2">
                    班主任
                  </label>
                  <TeacherSearchSelect
                    schoolId={parseInt(schoolId || '0')}
                    value={formData.headTeacherId ?? null}
                    initialDisplay={headTeacherDisplay}
                    onChange={(teacherId, t) => {
                      setFormData(prev => ({ ...prev, headTeacherId: teacherId ?? undefined }));
                      setHeadTeacherDisplay(t ? t.name : '');
                    }}
                    placeholder="搜索老师姓名或工号"
                  />
                  <p className="text-xs text-on-surface-variant mt-1.5 font-label">
                    设置班主任后，该老师会自动获得本班的查看/管理权限
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-on-surface font-label mb-2">
                    描述
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-3 py-2.5 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200 resize-none"
                    placeholder="请输入描述"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6 pt-6" style={{ borderTop: '1px solid rgba(171, 179, 183, 0.2)' }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2.5 bg-surface-container-high rounded-xl hover:bg-surface-container-low transition-colors font-medium"
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="px-4 py-2.5 gradient-primary text-on-primary rounded-xl shadow-teal transition-colors font-medium"
                >
                  {editingClass ? '更新' : '创建'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Excel导入学生模态框 */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-container-lowest rounded-3xl max-w-3xl w-full shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 sticky top-0 bg-surface-container-lowest" style={{ borderBottom: '1px solid rgba(171, 179, 183, 0.2)' }}>
              <h2 className="text-xl font-semibold text-on-surface font-headline">批量导入学生</h2>
              <p className="text-sm text-on-surface-variant font-body mt-1">支持一次导入多个班级的学生</p>
            </div>
            <div className="p-6">
              {!importResult ? (
                <>
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-on-surface font-label mb-2">Excel格式说明</h3>
                    <div className="bg-surface-container-low rounded-2xl p-4 text-sm text-on-surface-variant font-body">
                      <p className="font-semibold mb-2">支持两种格式：</p>
                      <div className="ml-4 space-y-3">
                        <div>
                          <p className="font-medium text-on-surface">格式1：标准模板（推荐）</p>
                          <div className="ml-2 space-y-1 text-xs font-label">
                            <p>• 列1: 身份证号码（必填）</p>
                            <p>• 列2: 姓名（必填）</p>
                            <p>• 列3: 班级ID（必填，可包含不同班级）</p>
                            <p>• 列4-8: 学号、电话、家长姓名、家长电话、备注（可选）</p>
                          </div>
                        </div>
                        <div>
                          <p className="font-medium text-on-surface">格式2：学校导入格式（自动识别）</p>
                          <div className="ml-2 space-y-1 text-xs font-label">
                            <p>• 第2行为表头，包含"学生姓名"和"身份证号码"及"班级"</p>
                            <p>• 从第3行开始为数据</p>
                            <p>• 自动提取姓名、身份证、班级、家长信息</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={handleDownloadTemplate}
                      className="mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium"
                    >
                      下载标准模板
                    </button>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-on-surface font-label mb-2">
                      选择Excel文件 <span className="text-danger-500">*</span>
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-2xl cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-colors flex items-center justify-center gap-2 text-on-surface-variant"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      {importFile ? (
                        <span className="text-on-surface font-medium">{importFile.name}</span>
                      ) : (
                        <span>点击选择Excel文件</span>
                      )}
                    </div>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 mb-6">
                    <p className="text-sm text-yellow-800 font-body">
                      <strong>注意：</strong>
                      系统将自动从身份证号码提取出生日期和性别信息，并为每个学生创建用户账号，初始密码为身份证后6位。已存在的学生将自动关联到目标班级。
                    </p>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowImportModal(false)}
                      className="px-4 py-2.5 bg-surface-container-high rounded-xl hover:bg-surface-container-low transition-colors font-medium"
                      disabled={importing}
                    >
                      取消
                    </button>
                    <button
                      onClick={handleImportSubmit}
                      disabled={!importFile || importing}
                      className="px-4 py-2.5 gradient-primary text-on-primary rounded-xl shadow-teal transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {importing ? '导入中...' : '开始导入'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-6">
                    <h3 className="text-lg font-medium text-on-surface font-headline mb-4">导入结果</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="bg-blue-50 rounded-2xl p-4">
                        <div className="text-sm text-on-surface-variant font-label">总行数</div>
                        <div className="text-2xl font-bold text-blue-600 font-headline">{importResult.totalRows}</div>
                      </div>
                      <div className="bg-green-50 rounded-2xl p-4">
                        <div className="text-sm text-on-surface-variant font-label">新增</div>
                        <div className="text-2xl font-bold text-green-600 font-headline">{importResult.successCount}</div>
                      </div>
                      {importResult.associatedCount > 0 && (
                        <div className="bg-indigo-50 rounded-2xl p-4">
                          <div className="text-sm text-on-surface-variant font-label">已关联</div>
                          <div className="text-2xl font-bold text-indigo-600 font-headline">{importResult.associatedCount}</div>
                        </div>
                      )}
                      <div className="bg-red-50 rounded-2xl p-4">
                        <div className="text-sm text-on-surface-variant font-label">失败</div>
                        <div className="text-2xl font-bold text-red-600 font-headline">{importResult.failedCount}</div>
                      </div>
                    </div>

                    {importResult.failedCount > 0 && (
                      <div className="rounded-2xl shadow-soft overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-surface-container-low">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-on-surface-variant font-label">行号</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-on-surface-variant font-label">姓名</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-on-surface-variant font-label">身份证</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-on-surface-variant font-label">错误信息</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {importResult.details
                              .filter(row => row.status === 'FAILED')
                              .map((row, idx) => (
                                <tr key={idx}>
                                  <td className="px-4 py-2 text-sm font-body">{row.rowNumber}</td>
                                  <td className="px-4 py-2 text-sm font-body">{row.name}</td>
                                  <td className="px-4 py-2 text-sm font-mono">{row.idCardNumber}</td>
                                  <td className="px-4 py-2 text-sm text-red-600 font-body">{row.errorMessage}</td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        setShowImportModal(false);
                        setImportResult(null);
                      }}
                      className="px-4 py-2.5 gradient-primary text-on-primary rounded-xl shadow-teal transition-colors font-medium"
                    >
                      完成
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassListPage;
